import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaVolumeUp, FaCheck, FaTimes, FaEye, FaEyeSlash, FaQuoteLeft, FaRandom, FaRedo } from 'react-icons/fa';
import { isAudioSupported, playSmartAudio } from '../utils/audioUtils';
import { getLessonById } from '../data/hafezPoems';
import type { HafezLesson } from '../data/allLessons';

interface Flashcard {
  id: string;
  type: 'word' | 'phrase' | 'line';
  persian: string;
  transliteration: string;
  english: string;
  context?: string;
  audioUrl?: string;
}

const HafezFlashcards: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<HafezLesson | null>(null);
  const [flashcards, setFlashcards] = useState<Flashcard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [knownCards, setKnownCards] = useState<Set<string>>(new Set());
  const [unknownCards, setUnknownCards] = useState<Set<string>>(new Set());
  const [isShuffled, setIsShuffled] = useState(false);
  const [studyMode, setStudyMode] = useState<'all' | 'unknown' | 'known'>('all');

  useEffect(() => {
    if (lessonId) {
      const foundLesson = getLessonById(lessonId);
      if (foundLesson) {
        setLesson(foundLesson);
        generateFlashcards(foundLesson);
      } else {
        navigate('/hafez');
      }
    }
  }, [lessonId, navigate]);

  const generateFlashcards = (hafezLesson: HafezLesson) => {
    const cards: Flashcard[] = [];

    // Generate word flashcards from word-by-word translations
    hafezLesson.poems.forEach((poem, poemIndex) => {
      poem.lines.forEach((line, lineIndex) => {
        line.wordByWord.forEach((word, wordIndex) => {
          cards.push({
            id: `word-${poemIndex}-${lineIndex}-${wordIndex}`,
            type: 'word',
            persian: word.persian,
            transliteration: word.transliteration,
            english: word.english,
            context: `${poem.title} - Line ${lineIndex + 1}`,
            audioUrl: line.audioUrl
          });
        });

        // Generate phrase flashcards for complete lines
        cards.push({
          id: `line-${poemIndex}-${lineIndex}`,
          type: 'line',
          persian: line.persian,
          transliteration: line.transliteration,
          english: line.english,
          context: `${poem.title}`,
          audioUrl: line.audioUrl
        });
      });
    });

    setFlashcards(cards);
  };

  const shuffleCards = () => {
    const shuffled = [...flashcards].sort(() => Math.random() - 0.5);
    setFlashcards(shuffled);
    setCurrentIndex(0);
    setIsShuffled(true);
    setShowAnswer(false);
  };

  const resetCards = () => {
    if (lesson) {
      generateFlashcards(lesson);
      setCurrentIndex(0);
      setIsShuffled(false);
      setShowAnswer(false);
      setKnownCards(new Set());
      setUnknownCards(new Set());
    }
  };

  const handleNext = () => {
    setShowAnswer(false);
    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    setShowAnswer(false);
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleMark = (know: boolean) => {
    const currentCard = flashcards[currentIndex];
    if (know) {
      setKnownCards(prev => {
        const newSet = new Set(prev);
        newSet.add(currentCard.id);
        return newSet;
      });
      setUnknownCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentCard.id);
        return newSet;
      });
    } else {
      setUnknownCards(prev => {
        const newSet = new Set(prev);
        newSet.add(currentCard.id);
        return newSet;
      });
      setKnownCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentCard.id);
        return newSet;
      });
    }
  };

  const filterCards = () => {
    if (studyMode === 'known') {
      return flashcards.filter(card => knownCards.has(card.id));
    } else if (studyMode === 'unknown') {
      return flashcards.filter(card => unknownCards.has(card.id));
    }
    return flashcards;
  };

  const filteredCards = filterCards();
  const currentCard = filteredCards[currentIndex];
  const progress = Math.round(((currentIndex + 1) / filteredCards.length) * 100);

  if (!lesson || !currentCard) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/hafez"
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-purple-500 dark:hover:text-purple-400 transition-colors"
          >
            <FaArrowLeft className="mr-2" />
            Back to Lessons
          </Link>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {lesson.title} - Flashcards
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {lesson.persianTitle} - کارت‌های آموزشی
            </p>
          </div>
          <div className="w-24"></div>
        </div>

        {/* Progress and Controls */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Progress
              </h3>
              <span className="text-lg font-bold text-purple-500">
                {currentIndex + 1} / {filteredCards.length}
              </span>
              <span className="text-lg font-bold text-purple-500">
                {progress}%
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={shuffleCards}
                className="p-2 text-gray-600 hover:text-purple-500 transition-colors"
                title="Shuffle cards"
              >
                <FaRandom />
              </button>
              <button
                onClick={resetCards}
                className="p-2 text-gray-600 hover:text-purple-500 transition-colors"
                title="Reset cards"
              >
                <FaRedo />
              </button>
            </div>
          </div>
          
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-4">
            <div 
              className="bg-gradient-to-r from-purple-500 to-indigo-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>

          {/* Study Mode Selector */}
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setStudyMode('all')}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                studyMode === 'all'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All Cards ({flashcards.length})
            </button>
            <button
              onClick={() => setStudyMode('unknown')}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                studyMode === 'unknown'
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Unknown ({unknownCards.size})
            </button>
            <button
              onClick={() => setStudyMode('known')}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                studyMode === 'known'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Known ({knownCards.size})
            </button>
          </div>
        </div>

        {/* Flashcard */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
            <div className="p-8">
              {/* Card Type Indicator */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
                  <FaQuoteLeft className="mr-2" />
                  {currentCard.type === 'word' ? 'Vocabulary Word' : 'Poetry Line'}
                </div>
                {currentCard.context && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {currentCard.context}
                  </p>
                )}
              </div>

              {/* Flashcard Content */}
              <div className="text-center mb-8">
                <div className="bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-8 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      {currentCard.type === 'word' ? 'Persian Word' : 'Persian Text'}
                    </h4>
                    {isAudioSupported() && currentCard.audioUrl && (
                      <button
                        onClick={() => playSmartAudio(undefined, currentCard.persian).catch(console.error)}
                        className="p-3 bg-purple-500 text-white rounded-full hover:bg-purple-600 transition-colors"
                      >
                        <FaVolumeUp />
                      </button>
                    )}
                  </div>
                  
                  <p className="text-4xl font-bold text-gray-900 dark:text-white mb-4 leading-relaxed">
                    {currentCard.persian}
                  </p>
                  
                  <p className="text-xl text-gray-600 dark:text-gray-400">
                    {currentCard.transliteration}
                  </p>
                </div>

                {/* Answer Section */}
                <div className="mb-6">
                  <button
                    onClick={() => setShowAnswer(!showAnswer)}
                    className="flex items-center mx-auto px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {showAnswer ? <FaEyeSlash className="mr-2" /> : <FaEye className="mr-2" />}
                    {showAnswer ? 'Hide' : 'Show'} Answer
                  </button>
                </div>

                {showAnswer && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 mb-6">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                      English Translation
                    </h4>
                    <p className="text-2xl text-gray-900 dark:text-white leading-relaxed">
                      {currentCard.english}
                    </p>
                  </div>
                )}

                {/* Knowledge Assessment */}
                {showAnswer && (
                  <div className="flex justify-center space-x-4 mb-6">
                    <button
                      onClick={() => handleMark(false)}
                      className="flex items-center px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold"
                    >
                      <FaTimes className="mr-2" />
                      Don't Know
                    </button>
                    <button
                      onClick={() => handleMark(true)}
                      className="flex items-center px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-semibold"
                    >
                      <FaCheck className="mr-2" />
                      Know It
                    </button>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center">
                <button
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                  className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                    currentIndex === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-500 text-white hover:bg-gray-600'
                  }`}
                >
                  Previous
                </button>

                <div className="flex space-x-2">
                  {filteredCards.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full ${
                        index === currentIndex
                          ? 'bg-purple-500'
                          : knownCards.has(filteredCards[index]?.id || '')
                          ? 'bg-green-500'
                          : unknownCards.has(filteredCards[index]?.id || '')
                          ? 'bg-red-500'
                          : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={handleNext}
                  disabled={currentIndex === filteredCards.length - 1}
                  className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                    currentIndex === filteredCards.length - 1
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Study Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-500">{flashcards.length}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Cards</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-500">{knownCards.size}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Known</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-500">{unknownCards.size}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Unknown</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HafezFlashcards; 